import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowRightArrowLeft";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { Spinner } from "../../../components/Spinner";
import { Modal } from "../../../components/modal/Modal";
import { FacebookMetaLeadFormDto } from "../../../model/integration/dto/facebook-meta/FacebookMetaLeadFormDto";
import { FacebookMetaLeadFormQuestionDto } from "../../../model/integration/dto/facebook-meta/FacebookMetaLeadFormQuestionDto";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { entryActions } from "../../../redux/entry/entry.slice";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { formActions } from "../../../redux/forms/form.slice";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useFacebookMetaConnect } from "./useFacebookMetaConnect";

/** antd hides its own loading icon behind AtiraSelect's arrow, so a select
 * that is fetching shows this instead, and stays shut until it has options
 */
const loadingSelectProps = (loading: boolean) =>
  loading
    ? { disabled: true, suffixIcon: <Spinner color="main" size="1rem" /> }
    : {};

const Section = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  border-radius: ${Rounded.lg};
  background-color: ${({ theme }) => theme.lightSub};
`;

const MappingRow = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};

  & > * {
    flex: 1;
    min-width: 0;
  }

  & > svg {
    flex: 0 0 auto;
  }
`;

/** Atira columns a Meta question type most likely belongs in, to save the user
 * mapping the obvious ones by hand
 */
const SUGGESTED_COLUMN: Record<string, string> = {
  EMAIL: "email",
  FULL_NAME: "name",
  PHONE: "phone",
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  /** The linked Meta form being edited, or null to link a new one */
  editFormId: string | null;
};

export const IntegrationAddModalFacebookMeta: React.FC<Props> = ({
  open,
  onClose,
  editFormId,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("integration.facebook-meta.modal.title")}
      width={"44rem"}
      destroyOnHidden
    >
      {open ? (
        <IntegrationAddModalFacebookMetaBody
          onClose={onClose}
          editFormId={editFormId}
        />
      ) : null}
    </Modal>
  );
};

const IntegrationAddModalFacebookMetaBody: React.FC<
  Pick<Props, "onClose" | "editFormId">
> = ({ onClose, editFormId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectFacebookMetaIntegration,
  );
  const atiraForms = useAppSelector(formSliceSelectors.selectForms);
  const atiraFormsLoading = useAppSelector(
    formSliceSelectors.selectFormsLoading,
  );
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const editing = useMemo(
    () =>
      integration?.config.forms?.find(
        ({ metaFormId }) => metaFormId === editFormId,
      ) || null,
    [integration?.config.forms, editFormId],
  );

  const { connect, connecting } = useFacebookMetaConnect();
  const [saving, setSaving] = useState(false);
  const [pageId, setPageId] = useState<string | undefined>(editing?.pageId);
  const [metaForms, setMetaForms] = useState<FacebookMetaLeadFormDto[]>([]);
  const [atiraFields, setAtiraFields] = useState<string[]>([]);
  const [metaFormsLoading, setMetaFormsLoading] = useState(false);
  const [metaFormId, setMetaFormId] = useState<string | undefined>(
    editing?.metaFormId,
  );
  const [formId, setFormId] = useState<string | undefined>(editing?.formId);
  /** Meta question key -> Atira column */
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const mappedFormIdRef = useRef<string | null>(null);

  const needsConnect = !integration || integration.config.needsReconnect;

  const columns = useMemo(
    () =>
      atiraFields.map((field) => {
        const column = tableSettings?.columns.find(
          (candidate) => candidate.field === field,
        );

        return {
          value: field,
          label:
            column && !column.custom
              ? t(`entries.table.settings.column.${field}`)
              : column?.label || field,
        };
      }),
    [atiraFields, tableSettings?.columns, t],
  );

  const questions: FacebookMetaLeadFormQuestionDto[] = useMemo(
    () => metaForms.find(({ id }) => id === metaFormId)?.questions || [],
    [metaForms, metaFormId],
  );

  useEffect(() => {
    dispatch(formActions.getForms({ userId }));
    dispatch(entryActions.getUserTableSettings({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (!integration?._id || !pageId || needsConnect) {
      setMetaForms([]);
      setAtiraFields([]);
      return;
    }

    let cancelled = false;
    setMetaFormsLoading(true);

    dispatch(
      integrationActions.getFacebookMetaForms({
        userId,
        integrationId: integration._id,
        pageId,
      }),
    )
      .unwrap()
      .then((response) => {
        if (!cancelled) {
          setMetaForms(response.forms);
          setAtiraFields(response.atiraFields);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setMetaForms([]);
          setAtiraFields([]);
          AtiraToast.apiError(error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMetaFormsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, integration?._id, needsConnect, pageId, userId]);

  useEffect(() => {
    if (!metaFormId || !questions.length) {
      mappedFormIdRef.current = null;
      setMapping({});
      return;
    }

    // Filled once per Meta form, so a late refresh of the table settings or
    // the integration never wipes choices already made
    if (mappedFormIdRef.current === metaFormId) {
      return;
    }

    mappedFormIdRef.current = metaFormId;
    const available = new Set(atiraFields);

    if (editing && editing.metaFormId === metaFormId) {
      const questionKeys = new Set(questions.map(({ key }) => key));

      // Questions removed from the Meta form and columns removed from the
      // table since linking are dropped: hidden, they could never be cleared
      setMapping(
        Object.fromEntries(
          Object.entries(editing.fieldsResolution)
            .filter(
              ([column, key]) => available.has(column) && questionKeys.has(key),
            )
            .map(([column, key]) => [key, column]),
        ),
      );
      return;
    }

    setMapping(
      Object.fromEntries(
        questions
          .map(({ key, type }) => [key, SUGGESTED_COLUMN[type]])
          .filter(([, column]) => column && available.has(column)),
      ),
    );
  }, [atiraFields, editing, metaFormId, questions]);

  const fieldsResolution = Object.fromEntries(
    Object.entries(mapping)
      .filter(([, column]) => column)
      .map(([key, column]) => [column, key]),
  );

  const canSave = Boolean(
    integration &&
      pageId &&
      metaFormId &&
      formId &&
      Object.keys(fieldsResolution).length,
  );

  const onSave = async () => {
    if (!integration || !pageId || !metaFormId || !formId) {
      return;
    }

    try {
      setSaving(true);

      await dispatch(
        integrationActions.linkFacebookMetaForm({
          userId,
          integrationId: integration._id,
          pageId,
          metaFormId,
          formId,
          fieldsResolution,
        }),
      ).unwrap();
      await dispatch(integrationActions.getUserIntegrations({ userId }));

      AtiraToast.success(t("integration.facebook-meta.modal.linked"));
      onClose();
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  if (needsConnect) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="l">
        <AtiraIcon icon={faFacebook} size="4x" color="facebook" />

        <Text align="center">
          {integration?.config.needsReconnect
            ? t("integration.facebook-meta.modal.reconnect.description")
            : t("integration.facebook-meta.modal.connect.description")}
        </Text>

        <Button
          icon={faFacebook}
          loading={connecting}
          loadingColor="white"
          onClick={connect}
          hover
        >
          {t("integration.facebook-meta.modal.connect.button")}
        </Button>

        <Info info={t("integration.facebook-meta.modal.leadAccess")} />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="m">
      <Section>
        <Text fontWeight="bold">
          {t("integration.facebook-meta.modal.source.title")}
        </Text>

        <AtiraSelect
          title={t("integration.facebook-meta.modal.page")}
          options={(integration.config.pages || []).map(({ id, name }) => ({
            value: id,
            label: name,
          }))}
          value={pageId}
          onChange={(value) => {
            setPageId(value);
            setMetaFormId(undefined);
          }}
          disabled={Boolean(editing)}
        />

        <AtiraSelect
          title={t("integration.facebook-meta.modal.metaForm")}
          options={metaForms.map(({ id, name, status }) => ({
            value: id,
            label: status && status !== "ACTIVE" ? `${name} (${status})` : name,
          }))}
          value={metaFormId}
          onChange={setMetaFormId}
          disabled={!pageId || Boolean(editing)}
          notFoundContent={t("integration.facebook-meta.modal.noMetaForms")}
          {...loadingSelectProps(metaFormsLoading)}
        />

        {editing ? null : (
          <Info info={t("integration.facebook-meta.modal.missingPage")} />
        )}
      </Section>

      {metaFormId && questions.length ? (
        <Section>
          <Text fontWeight="bold">
            {t("integration.facebook-meta.modal.destination.title")}
          </Text>

          <AtiraSelect
            title={t("integration.facebook-meta.modal.atiraForm")}
            options={atiraForms.map(({ _id, title }) => ({
              value: _id,
              label: title,
            }))}
            value={formId}
            onChange={setFormId}
            required
            {...loadingSelectProps(atiraFormsLoading && !atiraForms.length)}
          />

          <Text>{t("integration.facebook-meta.modal.map.description")}</Text>

          <Flex flexDirection="column" gap="s">
            {questions.map((question) => (
              <MappingRow key={question.key}>
                <Text>{question.label}</Text>
                <AtiraIcon icon={faArrowRightArrowLeft} color="darkSub" />
                <AtiraSelect
                  options={columns.filter(
                    ({ value }) =>
                      value === mapping[question.key] ||
                      !Object.values(mapping).includes(value),
                  )}
                  value={mapping[question.key]}
                  placeholder={t("integration.facebook-meta.modal.map.skip")}
                  onChange={(value) =>
                    setMapping((prev) => ({
                      ...prev,
                      [question.key]: value,
                    }))
                  }
                  allowClear
                />
              </MappingRow>
            ))}
          </Flex>
        </Section>
      ) : null}

      <Info info={t("integration.facebook-meta.modal.leadAccess")} />

      <Flex justifyContent="flex-end">
        <Button
          loading={saving}
          loadingColor="white"
          disabled={!canSave}
          onClick={onSave}
          hover
        >
          {t("common.save")}
        </Button>
      </Flex>
    </Flex>
  );
};
